import * as React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PageHeaderTone =
  | 'blue'
  | 'indigo'
  | 'emerald'
  | 'amber'
  | 'purple'
  | 'sky'
  | 'rose'
  | 'slate';

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  iconTone?: PageHeaderTone;
  tone?: PageHeaderTone;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
}

const toneGradients: Record<PageHeaderTone, string> = {
  blue: 'from-blue-500 to-blue-600 shadow-blue-500/25',
  indigo: 'from-indigo-500 to-indigo-600 shadow-indigo-500/25',
  emerald: 'from-emerald-500 to-teal-600 shadow-emerald-500/25',
  amber: 'from-amber-500 to-orange-600 shadow-amber-500/25',
  purple: 'from-purple-500 to-violet-600 shadow-purple-500/25',
  sky: 'from-sky-500 to-cyan-600 shadow-sky-500/25',
  rose: 'from-rose-500 to-pink-600 shadow-rose-500/25',
  slate: 'from-slate-600 to-slate-700 shadow-slate-600/25',
};

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      title,
      subtitle,
      description,
      icon: Icon,
      iconTone,
      tone,
      badge,
      actions,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const resolvedSubtitle = subtitle ?? description;
    const resolvedTone = iconTone ?? tone ?? 'blue';
    const gradient = toneGradients[resolvedTone] || toneGradients.blue;

    return (
      <div
        ref={ref}
        className={cn('space-y-4 pb-6 border-b border-border/80', className)}
        {...props}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            {Icon && (
              <div
                className={cn(
                  'rounded-xl bg-gradient-to-br p-3 text-white shadow-lg shrink-0',
                  gradient
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
            )}
            <div className="space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                  {title}
                </h1>
                {badge}
              </div>
              {resolvedSubtitle && (
                <p className="text-sm md:text-base text-muted-foreground">{resolvedSubtitle}</p>
              )}
            </div>
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2.5">{actions}</div>
          )}
        </div>
        {children && <div className="pt-2">{children}</div>}
      </div>
    );
  }
);
PageHeader.displayName = 'PageHeader';
